import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import ErrorDialog from "../ErrorDialog";
import Text from "../../atoms/Text";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { deviceDetect } from "react-device-detect";
import { mapStackTrace } from "sourcemapped-stacktrace";
/**
 * A component to render a custom error message or report form when an error
 * occurs in a React application. This component wraps the `ErrorBoundary`
 * component from the `react-error-boundary` package.
 *
 * @component
 * @param {IErrorFallbackProps} props The component props.
 * @param {Error} props.error The error object that caused the error boundary to trigger.
 * @param {string} props.errorReportingEndpoint The URL of the server endpoint to send error reports to.
 * @param {Function} props.resetErrorBoundary A function that can be called to reset the error boundary state.
 * @param {Function} [props.onSendReport] An optional callback function that is called when the user submits an error report.
 * @param {Function} [props.getReportData] An optional async function that can be used to get custom report data to include in error reports.
 * @param {KnownCip30WalletId} props.activeWallet The ID of the active wallet.
 * @param {string} props.walletAddress The address of the active wallet.
 * @param {Function} props.renderCollapsibleTrigger A function that returns a component to use as a trigger to show/hide the error report form.
 * @param {Function} props.renderFallbackMessage A function that returns a component to display as the error message when error reporting is disabled or fails.
 * @param {Object} props.i18n An object containing localized text strings for the component.
 * @param {string} props.i18n.cancelButtonText The text for the cancel button in the error report form.
 * @param {string} props.i18n.confirmButtonText The text for the submit button in the error report form.
 * @param {string} props.i18n.reportIdHeader The text for the "Report ID" label in the error report form.
 * @param {string} props.i18n.urlHeader The text for the "URL" label in the error report form.
 * @param {string} props.i18n.walletInfoHeader The text for the "Wallet Information" label in the error report form.
 * @param {string} props.i18n.walletAddressHeader The text for the "Wallet Address" label in the error report form.
 * @param {string} props.i18n.errorMessageHeader The text for the "Error Message" label in the error report form.
 * @param {string} props.i18n.errorStackHeader The text for the "Error Stack" label in the error report form.
 * @param {string} props.i18n.dateHeader The text for the "Date" label in the error report form.
 * @param {string} props.i18n.browserHeader The text for the "Browser" label in the error report form.
 * @param {string} props.i18n.osHeader The text for the "Operating System" label in the error report form.
 * @param {string} props.i18n.dialogTitle The title for the error report form dialog.
 * @param {boolean} [props.errorReportingEnabled=true] Whether error reporting is enabled or not.
 * @returns {JSX.Element} The rendered `ErrorFallback` component.
 */
const ErrorFallback = ({ error, errorReportingEnabled, errorReportingEndpoint, resetErrorBoundary, onSendReport, getReportData, activeWallet, walletAddress, renderCollapsibleTrigger, renderFallbackMessage, i18n, }) => {
    const [open, setOpen] = useState(false);
    const [errorStack, setErrorStack] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState();
    useEffect(() => {
        if (error.stack) {
            mapStackTrace(error.stack, (mappedStack) => {
                setErrorStack(mappedStack.join("\n"));
            });
        }
        else {
            setErrorStack("");
        }
    }, [error.stack]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const walletInfo = window.cardano?.[activeWallet];
    const defaultReportData = useMemo(() => {
        const date = new Date();
        const deviceInfo = deviceDetect(window.navigator.userAgent);
        const browser = `${deviceInfo.browserName} ${deviceInfo.browserFullVersion}`;
        const formattedDate = format(date, "MMM d, yyyy h:mm:ss aa z");
        const os = `${deviceInfo.osName} ${deviceInfo.osVersion}`;
        const reportId = crypto.randomUUID();
        const url = window.location.href;
        return {
            date,
            browser,
            formattedDate,
            os,
            reportId,
            url,
            localTimestamp: formattedDate,
        };
    }, []);
    const reportMessage = useMemo(() => {
        return `
          Report ID: ${reportData?.reportId}
          Url: ${reportData?.url}
          ${walletInfo
            ? `Wallet: ${walletInfo.name} ${walletInfo?.experimental?.appVersion}`
            : ""}
          ${walletAddress ? `Wallet address: ${walletAddress}` : ""}
          Error Message: ${error.message}
          ${errorStack || ""}
          Date: ${reportData?.localTimestamp}
          Browser: ${reportData?.browser}
          OS: ${reportData?.os}
        `;
    }, [reportData]);
    useEffect(() => {
        (async () => {
            const customReportData = getReportData ? await getReportData() : {};
            setReportData({
                ...defaultReportData,
                ...customReportData,
                message: reportMessage,
            });
        })();
    }, [getReportData, defaultReportData, reportMessage]);
    const handleSendReport = async () => {
        onSendReport && (await onSendReport());
        setIsLoading(true);
        await fetch(errorReportingEndpoint, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...reportData,
                stack: errorStack,
            }),
        });
        setIsLoading(false);
    };
    useEffect(() => {
        if (errorReportingEnabled && errorStack !== null) {
            handleSendReport();
        }
    }, [errorStack]);
    return (_jsx("div", { className: "px-6 pb-6 pt-24", children: _jsxs(ErrorDialog, { cancelButtonText: i18n.cancelButtonText, className: "mx-auto w-full max-w-xl", confirmButtonText: i18n.confirmButtonText, errorReport: _jsxs("div", { className: "grid gap-2", children: [_jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.reportIdHeader, ":"] }), reportData?.reportId] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.urlHeader, ":"] }), reportData?.url] }), walletInfo && (_jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.walletInfoHeader, ":"] }), walletInfo.name, " ", walletInfo.version] })), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.walletAddressHeader, ":"] }), walletAddress] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.errorMessageHeader, ":"] }), error.message] }), errorStack && (_jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.errorStackHeader, ":"] }), errorStack] })), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.dateHeader, ":"] }), reportData?.localTimestamp] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.browserHeader, ":"] }), reportData?.browser] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [i18n.osHeader, ":"] }), reportData?.os] })] }), isButtonDisabled: errorReportingEnabled || isLoading, isCancelButtonDisabled: isLoading, onCancel: resetErrorBoundary, onConfirm: handleSendReport, open: open, reportMessage: reportMessage, title: i18n.dialogTitle, trigger: renderCollapsibleTrigger(() => setOpen((prev) => !prev)), children: [_jsxs(Text, { tag: "div", children: [i18n.reportIdHeader, ": ", reportData?.reportId] }), renderFallbackMessage()] }) }));
};
export default ErrorFallback;
//# sourceMappingURL=ErrorFallback.js.map